/**
 * ManageMembersModal Component
 *
 * Modal for managing group users - add and remove users from the group.
 */

import React, { useState } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { useGroup } from '../hooks/useGroup';
import { useUsers } from '../../users/hooks/useUsers';
import { useAddUser } from '../hooks/useAddUser';
import { useRemoveUser } from '../hooks/useRemoveUser';
import type { GroupSanitized } from '../../../types/group.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface ManageMembersModalProps {
  group: GroupSanitized | null;
  tenantId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const ManageMembersModal: React.FC<ManageMembersModalProps> = ({
  group,
  tenantId,
  isOpen,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { data: groupDetails, isLoading: isLoadingGroup } = useGroup(
    tenantId,
    group?.groupId || ''
  );
  const { data: allUsers = [], isLoading: isLoadingUsers } = useUsers(tenantId);
  const addUserMutation = useAddUser(tenantId);
  const removeUserMutation = useRemoveUser(tenantId);

  if (!group) return null;

  const currentUserIds = groupDetails?.users?.map(u => u.userId) || group.users?.map(u => u.userId) || [];
  const availableUsers = allUsers.filter(
    user => !currentUserIds.includes(user.userId) && user.status === 'active'
  );

  const filteredAvailableUsers = availableUsers.filter(user =>
    searchQuery
      ? `${user.firstName} ${user.lastName} ${user.email}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      : true
  );

  const handleAddUser = async (userId: string, email: string) => {
    if (!group) return;
    try {
      await addUserMutation.mutateAsync({
        groupId: group.groupId,
        data: { userId, email },
      });
    } catch (error) {
      console.error('Failed to add user:', error);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!group) return;
    try {
      await removeUserMutation.mutateAsync({
        groupId: group.groupId,
        userId,
      });
    } catch (error) {
      console.error('Failed to remove user:', error);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>Manage Users - {group.groupName}</Modal.Header>

      <Modal.Body>
        <div className="space-y-6">
          {(addUserMutation.isError || removeUserMutation.isError) && (
            <Alert variant="error">
              {getErrorMessage(addUserMutation.error || removeUserMutation.error)}
            </Alert>
          )}

          {/* Current Users */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              Current Users ({groupDetails?.users?.length || 0})
            </h3>
            {isLoadingGroup ? (
              <div className="text-sm text-gray-500">Loading users...</div>
            ) : groupDetails && groupDetails.users && groupDetails.users.length > 0 ? (
              <div className="border border-gray-200 rounded-md divide-y divide-gray-200 max-h-48 overflow-y-auto">
                {groupDetails.users.map((user) => (
                  <div key={user.userId} className="p-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{user.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleRemoveUser(user.userId)}
                        loading={removeUserMutation.isPending}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">No users in this group</p>
            )}
          </div>

          {/* Available Users */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              Add Users
            </h3>
            <Input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mb-3"
            />
            {isLoadingUsers ? (
              <div className="text-sm text-gray-500">Loading users...</div>
            ) : filteredAvailableUsers.length > 0 ? (
              <div className="border border-gray-200 rounded-md divide-y divide-gray-200 max-h-64 overflow-y-auto">
                {filteredAvailableUsers.map((user) => (
                  <div key={user.userId} className="p-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {user.firstName} {user.lastName}
                      </p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleAddUser(user.userId, user.email)}
                      loading={addUserMutation.isPending}
                    >
                      Add
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">
                {searchQuery ? 'No users found matching your search' : 'No available users to add'}
              </p>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={onClose}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
